import { useQuery } from '@apollo/client'
import { useMemo } from 'react'
import { useParams } from 'react-router-dom'
import {
  GET_ALTERNATIVE_PRODUCTS,
  GET_FREQUENTLY_BOUGHT_TOGETHER,
  GET_LINKED_PRODUCTS,
  type IAlternativeProducts,
  type IAlternativeProductsVariables,
  type IFrequentlyBoughtTogether,
  type IFrequentlyBoughtTogetherVariables,
  type ILinkedProducts,
  type ILinkedProductsVariables,
  type IProductCatalog,
} from '@commerce/core'

/** A scroll row shows about this many before it stops being a row. */
const ROW_SIZE = 10

export interface UseRelatedProducts {
  /** "Opciones similares" — what to buy instead of this product. */
  alternatives: IProductCatalog[]
  /** "Se compran juntos" — what to buy with it. */
  companions: IProductCatalog[]
  loading: boolean
}

const usable = (
  products: readonly IProductCatalog[] | null | undefined,
  sku: string,
): IProductCatalog[] =>
  (products ?? []).filter(
    (product) => product?.sku && product.sku !== sku && product.isActive !== false,
  )

/**
 * The two related rows on a product page, from the server.
 *
 * What this replaces: `pickSimilarProducts`, which took the catalog the page had
 * already fetched and picked products from the same category. That is a
 * plausible heuristic, and it cost the product page a **full catalog query** —
 * every product this session can buy, downloaded to fill one scroll row.
 *
 * Two different questions, two different fields, and the difference matters to
 * a shopkeeper: `alternativeProducts` answers "instead of", which is what an
 * out-of-stock product needs, and `frequentlyBoughtTogether` answers "with",
 * which is what a full one needs.
 *
 * `linkedProducts` is the fallback for the first: it is authored rather than
 * computed, so a storefront with no alternatives model still has something to
 * show. It is session-wide, so this page filters the product itself out of it.
 *
 * Nothing is invented when all of them are empty — the row simply is not there.
 */
export const useRelatedProducts = (sku: string): UseRelatedProducts => {
  const { slug = '', sessionId = '' } = useParams()

  const alternatives = useQuery<IAlternativeProducts, IAlternativeProductsVariables>(
    GET_ALTERNATIVE_PRODUCTS,
    {
      variables: { storefrontName: slug, sessionUid: sessionId, skus: [sku] },
      skip: sku === '',
    },
  )
  const alternativeItems = usable(alternatives.data?.alternativeProducts, sku)
  const alternativesAnswered =
    !alternatives.loading && (alternatives.data !== undefined || alternatives.error)

  const linked = useQuery<ILinkedProducts, ILinkedProductsVariables>(GET_LINKED_PRODUCTS, {
    variables: { storefrontName: slug, sessionUid: sessionId },
    skip: sku === '' || !alternativesAnswered || alternativeItems.length > 0,
  })

  const companions = useQuery<IFrequentlyBoughtTogether, IFrequentlyBoughtTogetherVariables>(
    GET_FREQUENTLY_BOUGHT_TOGETHER,
    {
      variables: { storefrontName: slug, sessionUid: sessionId, skus: [sku], limit: ROW_SIZE },
      skip: sku === '',
    },
  )

  return {
    alternatives: useMemo(() => {
      const fromServer =
        alternativeItems.length > 0 ? alternativeItems : usable(linked.data?.linkedProducts, sku)
      return fromServer.slice(0, ROW_SIZE)
      // Same reason as `useSuggestedBasket`: the filtered arrays are rebuilt
      // every render, the query results are not.
    }, [alternatives.data, linked.data, sku]),

    companions: useMemo(
      () => usable(companions.data?.frequentlyBoughtTogether, sku).slice(0, ROW_SIZE),
      [companions.data, sku],
    ),

    loading: alternatives.loading || linked.loading || companions.loading,
  }
}
